import logger from 'logger';


export interface Grecaptcha {
  execute(
    key: string,
    options: {
      action: string;
    }
  ): Promise<string>
}

/**
 * @class reCAPTCHAv3Service handles google-recaptcha-v3 logic
 */
class reCAPTCHAv3Service {
  action: string = null;
  googleRecaptchaSrc = 'google.com';
  grecaptcha: Grecaptcha = null;
  reCaptchaKey: string = null;
  scriptId = 'google-recaptcha-v3';

  init = (reCaptchaKey: string, action: string): void => {
    if (typeof reCaptchaKey !== 'string' && typeof action !== 'string') {
      throw new Error(
        // eslint-disable-next-line max-len
        `reCaptchaKey and action are required to initialize reCAPTCHAv3Service and need to be a string. Provided: ${typeof reCaptchaKey}`
      );
    }

    this.reCaptchaKey = reCaptchaKey;
    this.action = action;
    this._injectScript();
  };

  /**
   * @method terminate - remove reCAPTCHA v3 script tag and related dom nodes from document
   */
  terminate = (): void => {
    const nodeBadge = document.querySelector('.grecaptcha-badge');
    if (nodeBadge && nodeBadge.parentNode) {
      document.body.removeChild(nodeBadge.parentNode);
    }

    const script = document.getElementById(this.scriptId);
    if (script) {
      script.remove();
    }
  };

  /**
   * @method getToken - execute reCAPTCHA v3 to retrieve token
   */
  getToken(): Promise<string> {
    if (this.grecaptcha) {
      return this._execute();
    }

    return Promise.reject();
  }

  /**
   * @method execute - call reCAPTCHA v3 execute action
   */
  async _execute(): Promise<string> {
    try {
      return await this.grecaptcha.execute(this.reCaptchaKey, { action: this.action });
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * @method _injectScript - inject reCAPTCHA v3 script in document head
   */
  _injectScript = (): void => {
    if (document.getElementById(this.scriptId)) {
      this._onLoad();

      return;
    }

    const head = document.getElementsByTagName('head')[0];
    const js = this._createScript();

    head.appendChild(js);
  };

  /**
   * @method _onLoad - set reCAPTCHA v3 object on window
   */
  _onLoad = (): void => {
    if (!window || !window.grecaptcha) {
      logger.warn('Script not available');

      return;
    }

    this.grecaptcha = window.grecaptcha;
  };

  /**
   * @method _createScript - create reCAPTCHA v3 script tag
   */
  _createScript = (): HTMLScriptElement => {
    const scriptElement = document.createElement('script');
    scriptElement.id = this.scriptId;
    scriptElement.src = `https://www.${this.googleRecaptchaSrc}/recaptcha/api.js?render=${this.reCaptchaKey}`;
    scriptElement.type = 'text/javascript';
    scriptElement.onload = this._onLoad;

    return scriptElement;
  };
}

export default reCAPTCHAv3Service;
